import React, {useRef, useState} from 'react';
import {
  useTheme,
  useDisclosure,
  Image,
  Box,
  Flex,
  Input,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import IconCustom from '../../components/Icon';
import {isEmpty} from 'lodash';
import {TextG12, TextG14, TextG18, TextG8} from '../typography';
import {useNavigate} from 'react-router-dom';
import BottomModalSheet, {BottomModalSheetRefProps} from '../Modals/BottomModalSheet';
import HeaderImg from '../../assets/images/support-header.png';
import useAuth from '../../hooks/useAuth';
import { BlueButton } from '../button';
import { browserDetected, deviceDetected, osVersion, isStandalone, isMobile } from '../../utils/deviceCheck';
import { submitSupportRequest } from '../../endpoints/api';
import { getAllLogs, getCurrentTraceId } from '../../utils/circularBuffer';
import logger from '../../utils/logger';

const ChatbotLauncher = ({
  customLauncher,
  id,
  customElement,
  setChatItem,
  disabled = false,
}: {
  customLauncher?: JSX.Element;
  id?: string;
  customElement?: React.ReactNode;
  setChatItem?: React.Dispatch<React.SetStateAction<boolean>>;
  disabled?: boolean;
}) => {
  const theme = useTheme();
  const {isOpen, onOpen, onClose} = useDisclosure();
  const navigate = useNavigate();
  const bottomModalSheetRef = useRef<BottomModalSheetRefProps>();
  const {user} = useAuth();
  const toast = useToast();
  
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [touchedFields, setTouchedFields] = useState({
    subject: false,
    body: false
  });

  const close = () => {
	if (window.ReactNativeWebView) {
		const message = {
			type: 'CHATBOT_CLOSED',
			message: 'Chatbot is closed'
		};
		(window as any).ReactNativeWebView.postMessage(JSON.stringify(message));
	}
    onClose();
    setChatItem && setChatItem(false);
    setSubject('');
    setBody('');
    setTouchedFields({ subject: false, body: false });
  };

  const open = () => {
	if (window.ReactNativeWebView) {
		const message = {
			type: 'CHATBOT_OPENED',
			message: 'Chatbot is opened'
		};
		(window as any).ReactNativeWebView.postMessage(JSON.stringify(message));
	}
    onOpen();
    setChatItem && setChatItem(true);
  };

  const handleSubmit = async () => {
    if (!subject.trim() || !body.trim() || !user) return;
    
    setIsSubmitting(true);
    
    try {
      const deviceType = deviceDetected();
      const metadata = {
        device: {
          type: deviceType,
          browser: browserDetected(),
          osVersion: osVersion(deviceType),
          isPwa: isStandalone(),
          isMobile: isMobile,
          screenSize: `${window.innerWidth}x${window.innerHeight}`,
          userAgent: navigator.userAgent,
          language: navigator.language
        },
        timestamp: new Date().toISOString()
      };
      
      // Log the support request submission
      logger.userAction('Submitting support request', {
        subject: subject.trim(),
        bodyLength: body.trim().length
      });
      
      // Get all accumulated logs (automatically merges native logs)
      const logs = await getAllLogs();
      
      // Get the current trace ID for this session
      const traceId = getCurrentTraceId() || `fallback-${Date.now()}`;
      
      await submitSupportRequest({
        email: user.email,
        subject: subject.trim(),
        body: body.trim(),
        metadata,
        traceId,
        logs
      });
      
      toast({
        title: 'Support Request Submitted',
        description: 'Thanks for reaching out! If necessary, we will get back to you soon.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      close();
      
      bottomModalSheetRef.current?.setState(false);
    } catch (error) {
      console.error('Failed to submit support request:', error);
      toast({
        title: 'Submission Failed',
        description: 'Failed to submit your request. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const iconStyle: React.CSSProperties = React.useMemo(
    () => ({
      fontSize: '1.3rem',
      height: '28px',
      marginBottom: '1px',
      paddingTop: '3px',
    }),
    [theme]
  );

  return (
    <BottomModalSheet
      disableDrag={true}
      disableScrollLocking={false}
      closeOnOverlayClick={false}
      triggerDisabled={disabled}
      ref={bottomModalSheetRef}
      onModalClose={close}
      triggerElement={
        <Box>
          {!isEmpty(customLauncher) ? (
            <Box id={id} onClick={disabled ? () => {} : open}>
              {customLauncher}
            </Box>
          ) : (
            <Flex alignItems="center" flexDirection={'column'}>
              {isOpen && customElement}
              <IconCustom
                id={id}
                onClick={disabled ? () => {} : open}
                iconName={'fi-rr-comment-user'}
                style={iconStyle}
              />
              <TextG8 fontWeight={isOpen ? '700' : '400'}>Support</TextG8>
            </Flex>
          )}
        </Box>
      }>
      <Flex flexDir={'column'} px={3} pb={4}>
        <TextG18 fontWeight="700">Help & Support</TextG18>
        {/* <TextG12
          my={2}
          onClick={() => {
            close();
            bottomModalSheetRef.current?.setState(false);
            navigate('/home?tutorial=t');
          }}
          color={theme.colors.blue}>
          View Quick Tutorial
        </TextG12> */}
        <Image src={HeaderImg} mr={-3} ml={-3} width="100vw" maxW="100vw" mb={4} />

        <Box px={3}>
          <TextG14 color="#272929" fontWeight="700" mb={2}>
            Submit a Support Request
          </TextG14>

          <Box mb={3}>
            <TextG12 color="#757D84" mb={1}>
              Subject
            </TextG12>
            <Input
              placeholder="Brief description of your issue"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              onBlur={() => setTouchedFields((prev) => ({...prev, subject: true}))}
              size="md"
              borderColor={touchedFields.subject && !subject.trim() ? 'red.300' : '#E5E5E5'}
            />
            {touchedFields.subject && !subject.trim() && (
              <TextG12 color="red.500" mt={1}>
                Please provide a subject for your request
              </TextG12>
            )}
          </Box>

          <Box mb={4}>
            <TextG12 color="#757D84" mb={1}>
              Details
            </TextG12>
            <Textarea
              placeholder="Please provide more details about your issue"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onBlur={() => setTouchedFields((prev) => ({...prev, body: true}))}
              size="md"
              borderColor={touchedFields.body && !body.trim() ? 'red.300' : '#E5E5E5'}
              rows={5}
            />
            {touchedFields.body && !body.trim() && (
              <TextG12 color="red.500" mt={1}>
                Please provide details about your issue
              </TextG12>
            )}
          </Box>

          {(!subject.trim() || !body.trim()) && (
            <TextG12 color="gray.500" mb={2} textAlign="center">
              Please complete all fields to submit your request
            </TextG12>
          )}

          <BlueButton
            width="100%"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            isDisabled={!subject.trim() || !body.trim()}>
            Submit Request
          </BlueButton>
        </Box>
      </Flex>
    </BottomModalSheet>
  );
};

export default ChatbotLauncher;
